import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

const Item = ({ item, index, moveItem }) => {
  const [{ isDragging }, drag] = useDrag({
    type: 'item',
    item: { index },
    collect: monitor => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [, drop] = useDrop({
    accept: 'item',
    hover: (draggedItem) => {
      if (draggedItem.index !== index) {
        moveItem(draggedItem.index, index);
        draggedItem.index = index;
      }
    },
  });

  return (
    <div
      ref={(node) => drag(drop(node))}
      style={{ opacity: isDragging ? 0.5 : 1, padding: '8px', border: '1px solid var(--border-color)', margin: '4px', cursor: 'move' }}
    >
      {item.name}
    </div>
  );
};

const MyTopics = () => {
  const [topics, setTopics] = useState([]);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [items, setItems] = useState([]);
  const [itemInput, setItemInput] = useState('');
  const [message, setMessage] = useState('');
  const [editTopicId, setEditTopicId] = useState(null);
  const [username, setUsername] = useState('');

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3002';

  const fetchTopics = async () => {
    const token = localStorage.getItem('token');
    try {
      const [topicsRes, userRes] = await Promise.all([
        axios.get(`${apiUrl}/api/topics/personal`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${apiUrl}/api/auth/verify`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setTopics(topicsRes.data);
      setUsername(userRes.data.user.name);
    } catch (err) {
      setMessage('Failed to fetch topics');
    }
  };

  useEffect(() => {
    fetchTopics();
  }, []);

  const moveItem = (fromIndex, toIndex) => {
    const updatedItems = [...items];
    const [movedItem] = updatedItems.splice(fromIndex, 1);
    updatedItems.splice(toIndex, 0, movedItem);
    setItems(updatedItems);
  };

  const handleAddItem = () => {
    if (itemInput.trim()) {
      const newItems = itemInput.split(',').map(item => item.trim()).filter(item => item).map(name => ({ name }));
      if (items.length + newItems.length > 200) {
        setMessage('Cannot add more than 200 items to a topic');
        return;
      }
      setItems([...items, ...newItems]);
      setItemInput('');
    }
  };

  const handleCreateOrUpdateTopic = async () => {
    const token = localStorage.getItem('token');
    try {
      if (!editTopicId && topics.length >= 20) {
        setMessage('Cannot create more than 20 topics, please delete to make more room.');
        return;
      }
      const payload = { name, description, items: items.map((item, index) => ({ name: item.name, position: index + 1 })) };
      const url = editTopicId ? `${apiUrl}/api/topics/personal/${editTopicId}` : `${apiUrl}/api/topics/personal`;
      const method = editTopicId ? 'put' : 'post';
      const response = await axios[method](url, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchTopics();
      setName('');
      setDescription('');
      setItems([]);
      setItemInput('');
      setEditTopicId(null);
      setMessage(editTopicId ? 'Topic updated successfully' : 'Topic created successfully');
    } catch (error) {
      setMessage('Failed to save topic: ' + (error.response?.data?.error || 'Unknown error'));
    }
  };

  const handleEditTopic = (topic) => {
    setName(topic.name);
    setDescription(topic.description || '');
    setItems(topic.items.map(item => ({ name: item.name })));
    setItemInput('');
    setEditTopicId(topic._id);
  };

  const handleDeleteTopic = async (topicId) => {
    if (!window.confirm('Are you sure you want to delete this topic?')) return;
    const token = localStorage.getItem('token');
    try {
      await axios.delete(`${apiUrl}/api/topics/personal/${topicId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchTopics();
      setMessage('Topic deleted successfully');
    } catch (error) {
      setMessage('Failed to delete topic: ' + (error.response?.data?.error || 'Unknown error'));
    }
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <h1>My Polls</h1>
      {message && <p>{message}</p>}
      <div>
        <input value={name} onChange={e => setName(e.target.value)} placeholder="Topic Name" maxLength="500" />
        <input value={description} onChange={e => setDescription(e.target.value)} placeholder="Description" maxLength="500" />
        <textarea
          value={itemInput}
          onChange={e => setItemInput(e.target.value)}
          placeholder="Add item, comma separated"
          maxLength="10000"
        />
        <button onClick={handleAddItem}>Add Item</button>
        <div>
          <h3>Items (Drag to reorder)</h3>
          {items.map((item, index) => (
            <Item key={item.name + index} item={item} index={index} moveItem={moveItem} />
          ))}
        </div>
        <button onClick={handleCreateOrUpdateTopic}>{editTopicId ? 'Update Topic' : 'Create Topic'}</button>
      </div>
      <h2>My Polls List</h2>
      {topics.length ? (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ border: '1px solid var(--border-color)', padding: '8px' }}>Name</th>
              <th style={{ border: '1px solid var(--border-color)', padding: '8px' }}>Description</th>
              <th style={{ border: '1px solid var(--border-color)', padding: '8px' }}>Items</th>
              <th style={{ border: '1px solid var(--border-color)', padding: '8px' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {topics.map(topic => (
              <tr key={topic._id}>
                <td style={{ border: '1px solid var(--border-color)', padding: '8px' }}>{topic.name}</td>
                <td style={{ border: '1px solid var(--border-color)', padding: '8px' }}>{topic.description || '-'}</td>
                <td style={{ border: '1px solid var(--border-color)', padding: '8px' }}>
                  {topic.items.map(item => item.name).join(', ')}
                </td>
                <td style={{ border: '1px solid var(--border-color)', padding: '8px' }}>
                  <button onClick={() => handleEditTopic(topic)}>Edit</button>
                  <button onClick={() => handleDeleteTopic(topic._id)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p>No topics available.</p>
      )}
      <h2>Topic Cards</h2>
      <div className="topic-cards">
        {topics.map(topic => (
          <div key={topic._id} className="topic-card">
            <h3>{topic.name}</h3>
            <h4>{topic.description || 'No description'}</h4>
            <h4>Owner: {username}</h4>
            <div>
              <h5>Items:</h5>
              <ol>
                {topic.items.slice(0, 25).map((item, index) => (
                  <li key={index}>{item.name}</li>
                ))}
              </ol>
            </div>
            <Link to={`/topic/${topic._id}`}>View Details</Link>
          </div>
        ))}
      </div>
    </DndProvider>
  );
};

export default MyTopics;